import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { ordersAPI, paymentsAPI, getApiError } from '../../services/api';
import { CreditCard, Wallet, ArrowLeft, Loader2, MapPin, FileText, ChevronRight } from 'lucide-react';

const Checkout = () => {
  const { user } = useAuth();
  const { cart, loadCart } = useCart();
  const navigate = useNavigate();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  const [shippingAddress, setShippingAddress] = useState({
    street: '', city: '', state: '', zip: '', country: '',
  });
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadCart();
    paymentsAPI.getMethods().then((res) => {
      const methods = res.data.methods || [];
      setPaymentMethods(methods);
      if (methods.length > 0) setSelectedMethod(methods[0].id);
    }).catch(() => {});
  }, [loadCart]);

  const handlePlaceOrder = async () => {
    if (!selectedMethod) return;
    setProcessing(true);
    setError('');

    try {
      const orderRes = await ordersAPI.create({
        payment_method: selectedMethod,
        shipping_address: shippingAddress,
        notes,
      });
      const order = orderRes.data;

      if (selectedMethod === 'stripe') {
        navigate(`/store/checkout/stripe/${order.id}`, { state: { order } });
      } else if (selectedMethod === 'commission_wallet') {
        const payRes = await paymentsAPI.payWithWallet({ order_id: order.id });
        if (payRes.data.success) {
          navigate(`/store/orders/${order.id}/confirmation`);
        } else {
          setError(payRes.data.message || 'Error al procesar el pago');
        }
      } else {
        navigate(`/store/orders/${order.id}/confirmation`);
      }
    } catch (err) {
      setError(getApiError(err, 'Error al crear la orden'));
    } finally {
      setProcessing(false);
    }
  };

  const methodIcons = {
    stripe: CreditCard,
    commission_wallet: Wallet,
    paypal: CreditCard,
  };

  if (cart.items.length === 0) {
    navigate('/store/cart');
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <motion.button
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/store/cart')}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-orange-600 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Volver al Carrito
      </motion.button>

      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-3 mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Checkout</h1>
        <div className="flex items-center gap-2 ml-auto">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                step >= s
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
              }`}>
                {s}
              </div>
              {s < 3 && <ChevronRight className="w-4 h-4 text-gray-400" />}
            </div>
          ))}
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3 space-y-6">
          {cart.items.some((i) => i.is_physical) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700"
            >
              <div className="flex items-center gap-2 mb-5">
                <MapPin className="w-5 h-5 text-orange-500" />
                <h2 className="text-lg font-semibold">Dirección de Envío</h2>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { key: 'street', placeholder: 'Dirección', cols: 2 },
                  { key: 'city', placeholder: 'Ciudad', cols: 1 },
                  { key: 'state', placeholder: 'Estado', cols: 1 },
                  { key: 'zip', placeholder: 'Código Postal', cols: 1 },
                  { key: 'country', placeholder: 'País', cols: 1 },
                ].map((f) => (
                  <input
                    key={f.key}
                    className={`p-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all ${f.cols === 2 ? 'col-span-2' : ''}`}
                    placeholder={f.placeholder}
                    value={shippingAddress[f.key]}
                    onChange={(e) => setShippingAddress({ ...shippingAddress, [f.key]: e.target.value })}
                  />
                ))}
              </div>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700"
          >
            <h2 className="text-lg font-semibold mb-5">Método de Pago</h2>
            <div className="space-y-3">
              {paymentMethods.map((method) => {
                const Icon = methodIcons[method.id] || CreditCard;
                return (
                  <motion.label
                    key={method.id}
                    whileHover={{ x: 4 }}
                    className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      selectedMethod === method.id
                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 shadow-md'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value={method.id}
                      checked={selectedMethod === method.id}
                      onChange={() => setSelectedMethod(method.id)}
                      className="text-orange-600 focus:ring-orange-500"
                    />
                    <Icon className={`w-6 h-6 ${selectedMethod === method.id ? 'text-orange-500' : 'text-gray-500'}`} />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">{method.label}</p>
                      <p className="text-sm text-gray-500">{method.description}</p>
                    </div>
                    {method.fee_percentage > 0 && (
                      <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
                        +{method.fee_percentage}% fee
                      </span>
                    )}
                  </motion.label>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700"
          >
            <div className="flex items-center gap-2 mb-5">
              <FileText className="w-5 h-5 text-orange-500" />
              <h2 className="text-lg font-semibold">Notas (opcional)</h2>
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
              rows={3}
              placeholder="Instrucciones especiales..."
            />
          </motion.div>
        </div>

        <div className="lg:col-span-2">
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 sticky top-24"
          >
            <h2 className="text-lg font-semibold mb-4">Resumen de Orden</h2>
            <div className="space-y-3 mb-6">
              {cart.items.map((item) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-between text-sm"
                >
                  <span className="text-gray-600 dark:text-gray-300 truncate max-w-[200px]">
                    {item.product_name} <span className="text-gray-400">x{item.quantity}</span>
                  </span>
                  <span className="font-medium">${item.total.toFixed(2)}</span>
                </motion.div>
              ))}
              <hr className="dark:border-gray-700" />
              <div className="flex justify-between font-bold text-lg">
                <span>Total</span>
                <span className="text-orange-600">${cart.subtotal.toFixed(2)}</span>
              </div>
            </div>

            {error && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-600 text-sm mb-4 bg-red-50 dark:bg-red-900/30 p-3 rounded-xl border border-red-200 dark:border-red-700"
              >
                {error}
              </motion.p>
            )}

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handlePlaceOrder}
              disabled={processing || !selectedMethod}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3.5 rounded-xl font-semibold hover:shadow-lg hover:shadow-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              {processing ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Procesando...</>
              ) : (
                `Pagar $${cart.subtotal.toFixed(2)}`
              )}
            </motion.button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
